import { createAction, Dispatch, AnyAction } from '@reduxjs/toolkit';

import { EventSourcePolyfill } from 'event-source-polyfill';
import CONFIG from '../../constants/sse';
import { liveBidsSlice } from '../../../redux/slices/liveBidsSlice';
import { sseConnectionSlice } from '../../../redux/slices/sse';
import ACTION, { EVENTS, SSEActions } from './types';
import { ROUTES, browserHistory } from '../../../components/Routes';
import { TradeState } from '../../types/trade';
import { COOKIES } from '../../constants/cookies';
import { getCookie } from '../cookies';
import { tradeChangeSlice } from '../../../redux/slices/tradeChange';
import { stateChangeSlice } from '../../../redux/slices/stateChangeSlice';
import isOmpPlatform from '../isOmpPlatform';
import { negotiationChangeSlice } from '../../../redux/slices/negotiation';

let eventSource: EventSourcePolyfill | Document | null = null;

interface IDetail {
  eventType: string;
  tradeId: number;
  tradeState?: number;
  [key: string]: any;
}

interface CustomEvent<T = any> extends Event {
  readonly detail: T;
  initCustomEvent(
    typeArg: string,
    canBubbleArg: boolean,
    cancelableArg: boolean,
    detailArg: T
  ): void;
}

const bidMessage = createAction<IDetail>(ACTION.BidEvent);
const tradeMessage = createAction<IDetail>(ACTION.TradeChangeEvent);
const stateChangeMessage = createAction<IDetail>(
  ACTION.TradeStateChangeAllEvent
);
const negotiationMessage = createAction<IDetail>(ACTION.NegotiationEvent);

const processEvent = (detail: IDetail, dispatch) => {
  const isActiveListView =
    browserHistory.location.pathname === ROUTES.BUYING_ACTIVE;
  const isVDP = browserHistory.location.pathname.startsWith(ROUTES.TRADE_URI);

  switch (detail?.eventType) {
    case EVENTS.BID_EVENT: {
      if (
        detail?.tradeId &&
        detail?.tradeState !== TradeState.closing &&
        (isActiveListView ||
          (isVDP &&
            browserHistory.location.pathname.endsWith(
              detail?.tradeId.toString()
            )))
      ) {
        dispatch(bidMessage(detail));
      }
      break;
    }
    case EVENTS.HEART_BEAT: {
      break;
    }
    case EVENTS.AUCTION_CHANGE: {
      // Backwards compatibility for the new TradeChangeEvent
      if (isActiveListView || isVDP) {
        dispatch(tradeMessage(detail));
      }
      break;
    }
    case EVENTS.TRADE_CHANGE: {
      if (isActiveListView || isVDP) {
        dispatch(tradeMessage(detail));
      }
      break;
    }
    case EVENTS.STATE_CHANGE: {
      if (
        [
          ROUTES.BUYING_ACTIVE,
          ROUTES.BUYING_UPCOMING,
          ROUTES.BUYING_PENDING,
          ROUTES.SELLING_PENDING
        ].includes(browserHistory.location.pathname) ||
        isVDP
      ) {
        if (detail && detail.tradeId) {
          dispatch(stateChangeMessage(detail));
        }
      }
      break;
    }
    case EVENTS.NEGOTIATION_EVENT: {
      if (
        [ROUTES.BUYING_PENDING, ROUTES.SELLING_PENDING].includes(
          browserHistory.location.pathname
        )
      ) {
        dispatch(negotiationMessage(detail));
      }
      break;
    }
    default:
      break;
  }
};

const init = dispatch => {
  const isOmp = isOmpPlatform();

  if (eventSource && !isOmp) {
    return;
  }

  if (isOmp) {
    eventSource = document as Document;

    eventSource.addEventListener('TREvent', (ev: CustomEvent<IDetail>) => {
      processEvent(ev.detail, dispatch);
    });
  } else {
    const authToken = getCookie(COOKIES.AUTH_TOKEN);

    if (authToken) {
      // Do not initialize SSE agent on public route
      eventSource = new EventSourcePolyfill(CONFIG.ENDPOINT, {
        headers: {
          authenticationToken: authToken
        }
      });

      eventSource.addEventListener('TREvent', (ev: { data: string }) => {
        const data = JSON.parse(ev.data);
        const bidData = JSON.parse(data?.data);
        const detail = { eventType: data?.type, ...bidData };
        processEvent(detail, dispatch);
      });
      eventSource.onerror = e => {
        dispatch(sseConnectionSlice.actions.setSseConnectionStatus(e));
      };
      eventSource.onopen = e => {
        if (e.status === 200) dispatch(sseConnectionSlice.actions.clear());
      };
    }
  }
};

const sseMiddleware = ({ dispatch }) => {
  init(dispatch);

  return (next: Dispatch<AnyAction>) => (action: SSEActions) => {
    const { type, payload } = action;

    if (type) {
      switch (type) {
        case ACTION.BidEvent: {
          return next(liveBidsSlice.actions.updateLiveBids(payload));
        }
        case ACTION.HeartbeatEvent: {
          break;
        }
        case ACTION.AuctionChangeEvent: {
          // Backwards compatibility for the new TradeChangeEvent
          return next(tradeChangeSlice.actions.updateMinBidThreshold(payload));
        }
        case ACTION.TradeChangeEvent: {
          return next(tradeChangeSlice.actions.updateMinBidThreshold(payload));
        }
        case ACTION.TradeStateChangeAllEvent: {
          return next(stateChangeSlice.actions.announceStateChange(payload));
        }
        case ACTION.NegotiationEvent: {
          return next(
            negotiationChangeSlice.actions.updateNegotiationState(payload)
          );
        }
        default:
          break;
      }
      return next(action);
    }
    return null;
  };
};

export default sseMiddleware;
